import { Injectable } from "@angular/core";
import { BehaviorSubject, Observable, of } from "rxjs";
import { tap, map, catchError, shareReplay, switchMap } from "rxjs/operators";
import { HttpClient } from "@angular/common/http";
import { CookieService } from "ngx-cookie-service";
import { Router } from "@angular/router";
import { AuthenticationService } from "./authentication.service";
import { radioTechnology, operators, environment } from "../../../environments/environment";
import { getName } from "country-list";
import { CdpConnectionSearchResponse } from "../models/layout/cdp-connection-search/cdp-connection-search-response";
import { CdpConnectionTagCloudResponse } from "../models/layout/cdp-connection-tag-cloud/cdp-connection-tag-cloud-response";
import { CdpConnectionAggregateArrayResponse } from "../models/layout/cdp-connection-aggregate/cdp-connection-aggregate-array/cdp-connection-aggregate-response";
import { CdpConnectionAggregateObjectResponse } from "../models/layout/cdp-connection-aggregate/cdp-connection-aggregate-object/cdp-connection-aggregate-response";
import { CdpConnectionAggregateNumberResponse } from "../models/layout/cdp-connection-aggregate/cdp-connection-aggregate-number/cdp-connection-aggregate-response";
import { CdpConnectionAggregateLocationsResponse } from "../models/layout/cdp-connection-aggregate-locations/cdp-connection-aggregate-locations-response";
import { LogListResponse } from "../models/layout/log-list/log-list-response";
import { CdpConnectionFindResponse } from "../models/layout/connection-find/connection-find-response";
import { Lwm2mDeviceFindResponse } from "../models/layout/lwm2m-device-find/lwm2m-device-find-response";
import { CdpResolveMccmncResponse } from "../models/layout/cdp-resolve-mccmnc/cdp-resolve-mccmnc-response";
import { PropertyHistoryResponse } from "../models/layout/property-history/property-history-response";
import { AttributeHistoryResponse } from "../models/layout/attribute-history/attribute-history-response";
import { LocationDecodeResponse } from "../models/layout/location-decode/location-decode-response";
import { LocationNetworkDecodeResponse } from "../models/layout/location-network-decode/location-network-decode-response";
import { ThingListResponse } from "../models/layout/thing-list/thing-list-response";
import { SessionInfoResponse, SESSION_INFO_COMMAND } from "../models/layout/session/info/SessionInfo";

@Injectable({
    providedIn: "root"
})
export class ApiService {
    execEndpoint = this.authenticationService.execEndpoint;
    appCenterUrl = this.authenticationService.appCenterUrl;
    globalSessionId: string = this.authenticationService.globalSessionId;

    private refreshTrigger = new BehaviorSubject<void>(undefined);
    public sessionApi: Observable<SessionInfoResponse> = this.refreshTrigger.pipe(
        // request to session api, using share replay to chache the result utitll the refreshSessionInfo is exetuted
        switchMap(() => {
            return this.executeCommand<SessionInfoResponse>(SESSION_INFO_COMMAND)
        }),
        shareReplay(1),
    );

    // Force refresh of sessionApi
    private refreshSessionInfo() {
        this.refreshTrigger.next();
    }

    constructor(
        private authenticationService: AuthenticationService,
        private cookieService: CookieService,
        private http: HttpClient,
        private router: Router,
    ) { }

    private hasSession(): boolean {
        const sessionId = this.cookieService.get("app-center-sessionId");
        return !!sessionId;
    }

    private withSessionAuth<T extends Record<string, any>>(payload: T): T {
        if (!this.hasSession()) {
            return payload;
        }

        return {
            ...payload,
            auth: { sessionId: this.globalSessionId }
        };
    }

    private createCommandRequest(command: string, params: any): Record<string, any> {
        const header = command === "api.authenticate" ? "auth" : "cmd";
        const payload = this.withSessionAuth({
            [header]: { command, params }
        } as Record<string, any>);

        return payload;
    }

    private postCommand<T>(command: string, params: any): Observable<T> {
        return this.http
            .post<T>(this.execEndpoint, this.createCommandRequest(command, params), {})
            .pipe(
                tap((response: T) => this.handleResponse(response, command)),
                catchError((err) => this.handleError(err))
            );
    }

    parseColors(name, type) {
        const env = type === "Radio technology" ? radioTechnology : operators;
        const value = Object.entries(env).find(([key]) => typeof name !== "undefined" && name.includes(key));
        return value ? `#${value[1]}` : "";
    }

    getData(cmd, params) {
        return this.authenticationService
            .execObservable(cmd, params, "all")
            .pipe(
                tap((response: any) => {
                    this.handleResponse(response, cmd)
                    return response;
                }),
                map((data) => {
                    if (data["cmd"] !== undefined && data["cmd"]["success"]) {
                        return data["cmd"]["params"];
                    }
                    return data["cmd"];
                }),
                // keep original error object intact instead of mapping it through cmd extraction
                catchError((err) => this.handleError(err))
            );

    }

    getConnectionFind(command: string, params: any): Observable<any> {
        return this.postCommand<any>(command, params);
    }

    getCdpEuiccFind(command: string, params: any): Observable<any> {
        return this.postCommand<any>(command, params);
    }

    getCdpEuiccMnoList(command: string, params: any): Observable<any> {
        return this.postCommand<any>(command, params);
    }

    getCdpConnectionSearchInfo(command: string, params: object): Observable<CdpConnectionSearchResponse> {
        return this.postCommand<CdpConnectionSearchResponse>(command, params);
    }

    getCdpConnectionTagCloudList(command: string, params: object): Observable<CdpConnectionTagCloudResponse> {
        return this.postCommand<CdpConnectionTagCloudResponse>(command, params);
    }

    getCdpConnectionAggregateArrayEntities(command: string, params: object): Observable<CdpConnectionAggregateArrayResponse> {
        return this.postCommand<CdpConnectionAggregateArrayResponse>(command, params);
    }

    getCdpConnectionAggregateObjectEntities(command: string, params: object): Observable<CdpConnectionAggregateObjectResponse> {
        return this.postCommand<CdpConnectionAggregateObjectResponse>(command, params);
    }

    getThingAggregateNumberEntity(command: string, params: object): Observable<CdpConnectionAggregateNumberResponse> {
        return this.postCommand<CdpConnectionAggregateNumberResponse>(command, params);
    }

    getCdpConnectionAggregateLocations(command: string, params: object): Observable<CdpConnectionAggregateLocationsResponse> {
        return this.postCommand<CdpConnectionAggregateLocationsResponse>(command, params);
    }

    getEventLogList(command: string, params: object, commandType: string): Observable<LogListResponse> {
        return this.postCommand<LogListResponse>(command, params);
    }

    getCdpConnectionFind(command: string, params: object): Observable<CdpConnectionFindResponse> {
        return this.postCommand<CdpConnectionFindResponse>(command, params);
    }

    getLwm2mDeviceFind(command: string, params: object): Observable<Lwm2mDeviceFindResponse> {
        return this.postCommand<Lwm2mDeviceFindResponse>(command, params);
    }

    getCdpResolveMccmnc(command: string, params: object): Observable<CdpResolveMccmncResponse> {
        return this.postCommand<CdpResolveMccmncResponse>(command, params);
    }

    getPropertyHistory(command: string, params: object, commandType: string): Observable<PropertyHistoryResponse> {
        return this.postCommand<PropertyHistoryResponse>(command, params);
    }

    getAttributeHistory(command: string, params: object): Observable<AttributeHistoryResponse> {
        return this.postCommand<AttributeHistoryResponse>(command, params);
    }

    getLocationDecode(command: string, params: object): Observable<LocationDecodeResponse> {
        return this.postCommand<LocationDecodeResponse>(command, params);
    }

    getLocationNetworkDecode(command: string, params: object): Observable<LocationNetworkDecodeResponse> {
        return this.postCommand<LocationNetworkDecodeResponse>(command, params);
    }

    getThingList(command: string, params: object, commandType: string): Observable<ThingListResponse> {
        return this.postCommand<ThingListResponse>(command, params);

    }

    getRssiColor(rssi) {
        if (rssi <= -115) return "red";
        if (rssi <= -105) return "orange";
        if (rssi <= -85) return "yellow";
        if (rssi === 0) return "grey";
        return "green";
    }

    getPercentColor(pct) {
        if (pct <= 20) return "red";
        if (pct <= 40) return "orange";
        if (pct <= 60) return "yellow";
        return "green";
    }

    getPercentileIcon(rank) {
        const normalizedRank = Number(rank) || 0;
        let picon = "fa-dizzy";
        let pcolor = "red";

        if (normalizedRank >= 75) {
            picon = "fa-grin";
            pcolor = "green";
        } else if (normalizedRank >= 50) {
            picon = "fa-smile";
            pcolor = "green";
        } else if (normalizedRank >= 40) {
            picon = "fa-meh";
            pcolor = "orange";
        } else if (normalizedRank >= 20) {
            picon = "fa-frown";
            pcolor = "orange";
        } else if (normalizedRank >= 10) {
            picon = "fa-angry";
            pcolor = "red";
        }

        const rankValue = Number(normalizedRank.toFixed(0));
        const j = rankValue % 10;
        const k = rankValue % 100;
        let rankStr = rankValue + "th";

        if (j === 1 && k !== 11) {
            rankStr = rankValue + "st";
        } else if (j === 2 && k !== 12) {
            rankStr = rankValue + "nd";
        } else if (j === 3 && k !== 13) {
            rankStr = rankValue + "rd";
        }

        return { rankStr, color: pcolor, icon: picon };
    }

    convertCountryCode(code) {
        switch (code) {
            case "kr":
                return "South Korea";
            case "ru":
                return "Russia";
            case "us":
                return "USA";
            case "gb":
                return "United Kingdom";
            default:
                return getName(code);
        }
    }

    /**
     * Function to execute an http requet to server
     * all call all made by post
     * if present an app-center-sessionId will be added to the http request
     * if envriroment useMock is set to true data from mock (if present) will be used instead of executing the http request
     *
     * @param command command of api call
     * @param params params object passed to api call
     * @returns response of api call
     */
    get<T, S = unknown>(command: string, params: S = null): Observable<T> {
        const cmdObj = this.withSessionAuth({
            cmd: { command, params }
        });

        return this.http
            .post<T>(this.execEndpoint, cmdObj, {})
            .pipe(
                tap((response: T) => this.handleResponse(response, command)),
                catchError((err) => this.handleError(err))
            );
    }


    /**
     * Function to execute an http requet to server
     * all call all made by post
     * if present an app-center-sessionId will be added to the http request
     * if envriroment useMock is set to true data from mock (if present) will be used instead of executing the http request
     *
     * @param command command of api call
     * @param params params object passed to api call
     * @returns response of api call
     */
    executeCommand<T extends BaseResponseType, S = unknown>(command: string, params?: S): Observable<T> {
        let payload: BaseRequestType<S> = {
            cmd: {
                command,
                params
            }
        }
        return this.executeMultipleCommandRequest<BaseRequestType<S>, T>(payload)
    }

    executeMultipleCommandRequest<T, R extends { [key in keyof T]: any }>(data): Observable<R> {
        const payload = {
            ...data,
            ...(this.authenticationService?.globalSessionId ? { auth: { sessionId: this.authenticationService.globalSessionId } } : {})
        };
        return this.http.post<R>(this.authenticationService.execEndpoint, payload);
    }

    handleResponse(response, command) {
        // if success is false and command is not session.info refresh the session to check it is still open
        if (response["success"] === false && command != SESSION_INFO_COMMAND) {
            this.refreshSessionInfo();
        }
    }

    handleError(err) {
        return of(err);
    }

}

export type BaseResponseType = {
    cmd: unknown
};

export type BaseRequestType<S> = {
    cmd: {
        command: string,
        params: S
    }
}
